/** What the recipe editor works on: a new recipe, one being edited, or an import. */
import type { Confidence, RecipeInput, RecipeView } from '../../../shared/api';
import type { Serving } from '../../../shared/foods';
import { amountForFood } from '../../../shared/ingredientLine';
import { lineUnit, unitIfOffered, type KitchenUnit } from '../../../shared/kitchenMeasures';
import type { KitchenUnits } from '../../../shared/units';
import { parseDecimal } from '../../../shared/numbers';
import { BUILTIN_BY_KEY, formatAmount, type NutrientMap } from '../../../shared/nutrients';
import { fitServings, IMPORT_LIMITS, stepsToNotes, type ImportedRecipe, type ImportOrigin, type SiteNutrition } from '../../../shared/recipeImport';
import { MAX_ENTRY_GRAMS, RECIPE_LIMITS, safeHttpUrl } from '../../../shared/recipes';
import type { PickedFood } from '../log/logItem';

/** How the AI matched an imported ingredient, flagged the way the Log sheet's review items are. */
export interface IngredientReview {
  confidence: Confidence;
  /** Nutrition from an AI estimate rather than a known food. */
  estimated: boolean;
  /** The known food it was matched to, when that name differs. */
  matchName: string | null;
  note: string | null;
}

export interface DraftIngredient {
  /** For list rendering only. */
  key: string;
  foodRef: string | null;
  name: string;
  brand: string | null;
  grams: number;
  /** The kitchen unit its amount is shown and saved in ("cup" for "1/2 cup sugar"); left out for grams. */
  unit?: KitchenUnit;
  per100: NutrientMap;
  servings: Serving[];
  /** Imported ingredients: the line it came from ("2 cups all-purpose flour"). It stays when the person picks another food. */
  line?: string;
  /** Imported ingredients: where the line was in the recipe, so a line matched later goes back in its place. */
  position?: number;
  /** How the AI matched an imported ingredient (or a best guess by name), until the person picks another food. */
  review?: IngredientReview;
  /** The amount was worked out roughly from the line (about 1 g per ml, or one of the food's sizes): "Check the amount". */
  checkAmount?: boolean;
  /** The person set the amount themselves: picking another food for the line keeps it. */
  edited?: boolean;
  /** The person chose the unit themselves (grams too): another food for the line keeps it where it can. */
  unitPicked?: boolean;
}

/** An imported line that isn't matched to a food yet: the person picks one, or removes the line. */
export interface UnmatchedLine {
  key: string;
  line: string;
  /** Grams from an explicit weight in the line ("200 g"), to start its amount from. */
  gramsHint: number | null;
  /** Where the line was in the recipe. */
  position: number;
}

/**
 * One row of the editor's ingredient list, in the recipe's order: a food with its amount, or an
 * imported line still to match. A line that gets a food stays in its place, and remembers what
 * it was (`previous`), so the person can undo the pick.
 */
export type IngredientRow = { kind: 'food'; food: DraftIngredient; previous?: IngredientRow } | { kind: 'line'; line: UnmatchedLine };

export const rowKey = (row: IngredientRow) => (row.kind === 'food' ? row.food.key : row.line.key);

/** The draft's ingredients and its lines still to match, as one list in the recipe's order. */
export function rowsFrom(draft: Pick<RecipeDraft, 'ingredients' | 'unmatched'>): IngredientRow[] {
  const rows: IngredientRow[] = draft.ingredients.map((food) => ({ kind: 'food', food }));
  for (const line of draft.unmatched ?? []) {
    // Before the first row that came after it in the recipe.
    const at = rows.findIndex((r) => (r.kind === 'food' ? r.food.position !== undefined && r.food.position > line.position : r.line.position > line.position));
    rows.splice(at === -1 ? rows.length : at, 0, { kind: 'line', line });
  }
  return rows;
}

export const foodsOf = (rows: readonly IngredientRow[]) => rows.flatMap((r) => (r.kind === 'food' ? [r.food] : []));
export const linesOf = (rows: readonly IngredientRow[]) => rows.flatMap((r) => (r.kind === 'line' ? [r.line] : []));

/** `food` in the place of the row `key` (a line it was picked for, or the food it replaces), which it remembers for Undo. */
export function pickInRow(rows: readonly IngredientRow[], key: string, food: DraftIngredient): IngredientRow[] {
  return rows.map((row) => (rowKey(row) === key ? { kind: 'food', food, previous: row } : row));
}

/** The row `key` back to what it was before its food was picked: the line still to match, or the food before. */
export function undoPick(rows: readonly IngredientRow[], key: string): IngredientRow[] {
  return rows.map((row) => (rowKey(row) === key && row.kind === 'food' && row.previous ? row.previous : row));
}

/** The row `key` moved up (-1) or down (1) a place, or null at the top or bottom. */
export function moveRow(rows: readonly IngredientRow[], key: string, delta: -1 | 1): IngredientRow[] | null {
  const from = rows.findIndex((r) => rowKey(r) === key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= rows.length) return null;
  const next = [...rows];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}

/** A food picked for an ingredient: "entry:<id>" (logged without a food) isn't a food to point at. */
export function asIngredient(food: PickedFood, key: string, grams: number): DraftIngredient {
  return {
    key,
    foodRef: food.ref.startsWith('entry:') ? null : food.ref,
    // Within what the server takes (a long FatSecret name, say), so saving can't fail on it.
    name: food.name.slice(0, 120),
    brand: food.brand?.slice(0, 80) ?? null,
    grams: Math.min(MAX_ENTRY_GRAMS, grams),
    per100: food.per100,
    servings: food.servings,
  };
}

/**
 * A food picked for an imported line, keeping the line so the card still says where it came
 * from. Its grams come from the line (shared/ingredientLine.ts): the line's own weight, else the
 * food's size in the line's unit, else its density. When the line can only give a rough amount,
 * it's marked "Check the amount". Picked in place of `current` (Replace), the amount the person
 * set stays, and so does the current amount when the line can only give a rough one.
 *
 * Its unit is the line's ("1/2 cup sugar" in cups) when the food can show it in these kitchen
 * measures, else grams. A unit the person chose for the row (grams too) stays instead, when the
 * new food can show it; when it can't, the line's unit (or grams) again.
 */
export function pickForLine(
  food: PickedFood,
  from: { line: string; position?: number },
  key: string,
  current?: DraftIngredient,
  units: KitchenUnits = 'us',
): DraftIngredient {
  const amount = amountForFood(from.line, food);
  let grams = amount.grams;
  let checkAmount: true | undefined = amount.check || undefined;
  if (current?.edited) {
    grams = current.grams;
    checkAmount = undefined;
  } else if (current && amount.check) {
    grams = current.grams;
  }
  // Judged by its sizes alone, as the editor's amount control, its saving and the recipe page all do: they don't know a drink from a food.
  const sizes = { servings: food.servings };
  const kept = current?.unitPicked === true && (current.unit === undefined || unitIfOffered(current.unit, sizes, units) !== null);
  const unit = kept ? current.unit : (unitIfOffered(lineUnit(from.line), sizes, units) ?? undefined);
  return {
    ...asIngredient(food, key, grams),
    ...(unit ? { unit } : {}),
    line: from.line,
    position: from.position,
    checkAmount,
    edited: current?.edited,
    ...(kept ? { unitPicked: true } : {}),
  };
}

/** What the editor shows about an import, beside the draft itself. */
export interface ImportInfo {
  origin: ImportOrigin;
  /** The site it came from ("bbcgoodfood.com"), for links. */
  site: string | null;
  /** The site's own nutrition per serving, shown next to Grams' numbers as a check. */
  siteNutrition: SiteNutrition | null;
  /** Why the ingredients aren't matched by the AI (AI off, say), and what was matched by name instead, or null. */
  notice: string | null;
  /**
   * Who matched the ingredients to foods: the AI, a match by name (best guesses) without it, or
   * both (the AI, then names for the lines it found no food for).
   */
  matchedBy: 'ai' | 'name' | 'both' | null;
  /** The yield as the source wrote it ("Makes 12 cookies"). */
  yieldText: string | null;
  /** Steps that didn't fit in the notes (they're cut at whole steps). */
  stepsLeft: number;
}

export interface RecipeDraft {
  name: string;
  /** Null until chosen: a new recipe asks how many servings it makes. */
  servings: number | null;
  cookedGrams: number | null;
  notes: string;
  /** The page it came from. The editor doesn't show it; saving keeps it. */
  sourceUrl: string | null;
  ingredients: DraftIngredient[];
  /** Imported lines still to match to a food. */
  unmatched?: UnmatchedLine[];
  /** Set when the draft came from an import. */
  imported?: ImportInfo;
}

let seq = 0;
export const ingredientKey = () => `ingredient-${++seq}`;

/** "www.bbcgoodfood.com" → "bbcgoodfood.com", for "From …". */
export function siteOf(link: string | null): string | null {
  const href = link ? safeHttpUrl(link) : null;
  return href ? new URL(href).hostname.replace(/^www\./, '') : null;
}

/** Punctuation that ends a sentence or a quote, in Latin and CJK text: never the end of a link. */
const LINK_END = /[.,;:!?'"\]>“”‘’«»‹›…。，、；：！？）】」』]+$/u;

/** "…/Chili_(food))." → "…/Chili_(food)": punctuation after a link in a sentence isn't part of it. */
function trimLinkEnd(link: string): string {
  let out = link.replace(LINK_END, '');
  while (out.endsWith(')') && out.split('(').length < out.split(')').length) out = out.slice(0, -1).replace(LINK_END, '');
  return out;
}

/**
 * The link to import from what was typed or pasted: "bbcgoodfood.com/…" gets https://,
 * and shared text ("Try this: “https://…”") gives its first link. Null when there's none.
 */
export function importLink(text: string): string | null {
  const t = text.trim();
  // A link ends at a space, a quote or CJK punctuation (text often follows a link with no space in Chinese or Japanese).
  const shared = /https?:\/\/[^\s<>"“”‘’«»…。，、；！？（）【】「」『』]+/iu.exec(t)?.[0];
  let candidate: string | null = null;
  if (shared) candidate = trimLinkEnd(shared);
  // Typed without https://, it has to name a website: "1.5" would read as the address 1.0.0.5.
  else if (t !== '' && !/\s/.test(t)) candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(t) ? t : /^[^/?#]*\p{L}/u.test(t) ? `https://${t}` : null;
  const href = candidate ? safeHttpUrl(candidate) : null;
  if (!href || href.length > IMPORT_LIMITS.url) return null;
  // A name without a dot ("chili") isn't a website.
  return new URL(href).hostname.includes('.') ? href : null;
}

/**
 * An import as an editor draft: matched ingredients with their review flags, the lines
 * still to match, the steps as notes, and the site's own nutrition to compare with.
 * Each ingredient starts in its line's unit when its food can show it in these kitchen
 * measures, else in grams. Kept within what a recipe can hold, so saving can't fail on it.
 */
export function draftFromImport(imported: ImportedRecipe, units: KitchenUnits = 'us'): RecipeDraft {
  const ingredients: DraftIngredient[] = [];
  const unmatched: UnmatchedLine[] = [];
  for (const [position, { line, item, gramsHint, roughAmount }] of imported.ingredients.slice(0, IMPORT_LIMITS.ingredients).entries()) {
    if (!item) {
      unmatched.push({ key: ingredientKey(), line, gramsHint, position });
      continue;
    }
    const unit = unitIfOffered(lineUnit(line), { servings: item.servings }, units);
    ingredients.push({
      key: ingredientKey(),
      foodRef: item.foodRef,
      name: item.name.slice(0, RECIPE_LIMITS.name),
      brand: item.brand?.slice(0, 80) ?? null,
      grams: Math.min(MAX_ENTRY_GRAMS, item.grams),
      ...(unit ? { unit } : {}),
      per100: item.per100,
      servings: item.servings,
      line,
      position,
      review: { confidence: item.confidence, estimated: item.estimated, matchName: item.matchName, note: item.note },
      // A best guess's rough amount says "Check the amount" until the person sets it.
      ...(roughAmount ? { checkAmount: true } : {}),
    });
  }
  const { notes, left } = stepsToNotes(imported.steps);
  // The editor doesn't show the link, so one a recipe can't keep would make saving fail with no way to fix it.
  const link = imported.sourceUrl ? safeHttpUrl(imported.sourceUrl) : null;
  return {
    name: imported.name.slice(0, RECIPE_LIMITS.name),
    servings: fitServings(imported.servings),
    cookedGrams: null,
    notes,
    sourceUrl: link && link.length <= RECIPE_LIMITS.sourceUrl ? link : null,
    ingredients,
    unmatched,
    imported: {
      origin: imported.origin,
      site: siteOf(imported.sourceUrl),
      siteNutrition: imported.siteNutrition,
      notice: imported.notice,
      matchedBy: imported.matchedByAI ? (imported.matchedByName ? 'both' : 'ai') : imported.matchedByName ? 'name' : null,
      yieldText: imported.yieldText,
      stepsLeft: left,
    },
  };
}

/** Whether an ingredient needs a second look: the AI wasn't sure, or its nutrition is an estimate. */
export function needsCheck(review: IngredientReview | undefined): boolean {
  return review !== undefined && (review.confidence === 'low' || review.estimated);
}

export interface ComparisonRow {
  key: string;
  label: string;
  /** "312 kcal", "28 g". */
  site: string;
  /** The ingredients' number for one serving, or "—" (no ingredients or servings yet, or none has it). */
  ours: string;
  /** Only some ingredients have it, so the real amount may be higher. */
  partial: boolean;
}

/** One serving of the ingredients so far, with how many of them had each nutrient. */
export interface OurServing {
  perServing: NutrientMap;
  known: Record<string, number>;
  count: number;
}

/**
 * The nutrients the site published, beside the ingredients' numbers for one serving, in the
 * order extraction reads them (calories, then macros, then the rest). `linesLeft`: imported
 * lines not matched yet, which the ingredients' numbers can't include.
 */
export function siteComparison(site: NutrientMap, ours: OurServing | null, linesLeft = 0): ComparisonRow[] {
  const rows: ComparisonRow[] = [];
  for (const [key, value] of Object.entries(site)) {
    const def = BUILTIN_BY_KEY.get(key);
    if (!def) continue;
    const show = (n: number | undefined) => (n === undefined ? '—' : `${formatAmount(n, def)} ${def.unit}`);
    // No ingredient with a value for it: unknown, not zero.
    const mine = ours ? ours.perServing[key] : undefined;
    const known = ours && Object.hasOwn(ours.known, key) ? ours.known[key]! : 0;
    const partial = mine !== undefined && ours !== null && (known < ours.count || linesLeft > 0);
    rows.push({ key, label: def.name, site: show(value), ours: show(mine), partial });
  }
  return rows;
}

/** Starting from scratch. */
export function emptyDraft(): RecipeDraft {
  return { name: '', servings: null, cookedGrams: null, notes: '', sourceUrl: null, ingredients: [] };
}

export function draftFrom(recipe: RecipeView): RecipeDraft {
  return {
    name: recipe.name,
    servings: recipe.servings,
    cookedGrams: recipe.cookedGrams,
    notes: recipe.notes,
    sourceUrl: recipe.sourceUrl,
    ingredients: recipe.ingredients.map((i) => ({
      key: ingredientKey(),
      foodRef: i.foodRef,
      name: i.name,
      brand: i.brand,
      grams: i.grams,
      ...(i.unit ? { unit: i.unit } : {}),
      per100: i.per100,
      servings: i.servings,
    })),
  };
}

/** Servings as typed: a whole number within the limits (digits only, so "1e2" or "0x10" aren't 100 and 16), else null. */
export function parseServings(text: string): number | null {
  const t = text.trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n >= RECIPE_LIMITS.servings.min && n <= RECIPE_LIMITS.servings.max ? n : null;
}

/** The cooked weight as typed. Empty is fine (no cooked weight); anything else must be grams within the limits. */
export function parseCooked(text: string): { grams: number | null; valid: boolean } {
  if (text.trim() === '') return { grams: null, valid: true };
  const n = parseDecimal(text);
  return n !== null && n >= 1 && n <= RECIPE_LIMITS.cookedGrams ? { grams: n, valid: true } : { grams: null, valid: false };
}

/** The request body for saving a draft whose servings were checked. */
export function toInput(draft: RecipeDraft & { servings: number }): RecipeInput {
  return {
    name: draft.name.trim(),
    servings: draft.servings,
    cookedGrams: draft.cookedGrams,
    notes: draft.notes.trim(),
    sourceUrl: draft.sourceUrl,
    ingredients: draft.ingredients.map(({ foodRef, name, brand, grams, unit, per100, servings }) => ({ foodRef, name, brand, grams, unit: unit ?? null, per100, servings })),
  };
}
